import { type BioV2VisitorCanEditQuery } from '#app/__generated__/relay/BioV2VisitorCanEditQuery.graphql.js';
import { useProfileMagicBioIndicator } from '#app/components/ContraMagic/useProfileMagicBioIndicator.js';
import { InlineEditContainer } from '#app/components/InlineEditContainer/InlineEditContainer.js';
import { useHasUserInterfaceFlags } from '#app/hooks/useHasUserInterfaceFlags.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { omitEmojisFromText } from '#app/utilities/omitEmojisFromText.js';
import { InlineEditingEmptyPlaceholder } from '../InlineEditingEmptyPlaceholder.js';
import { BioTextRegular } from './BioV2.styles.js';
import { EditIcon } from '@contra/icons/icons';
import { useMount } from '@contra/react-hooks/useMount';
import { graphql, useLazyLoadQuery } from '@contra/relay';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { styled } from 'styled-components';

const LazyBioV2InlineEditing = lazy(async () => {
  return {
    default: (await import('./BioV2InlineEditing.js')).BioV2InlineEditing,
  };
});

const BioContainer = styled.div<{ $startRevealBioAnimation?: boolean }>`
  text-align: left;
  width: 100%;
`;

type BioV2VisitorCanEditProps = { readonly bio: null | string };

export const BioV2VisitorCanEdit = ({ bio }: BioV2VisitorCanEditProps) => {
  const [isInlineEditing, setIsInlineEditing] = useState(false);

  const [urlSearchParameters] = useURLParameters('/@username');
  const addBio = urlSearchParameters.addBio;

  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const data = useLazyLoadQuery<BioV2VisitorCanEditQuery>(
    graphql`
      query BioV2VisitorCanEditQuery {
        visitor {
          userAccount {
            contraMagicBioSuggestions {
              id
              isSelected
              suggestion
            }
            contraMagicQuestionnaireResponse {
              whatMakesYouUnique
              whatServicesDoYouOffer
            }
          }
        }
      }
    `,
    {},
  );

  const bioSuggestions =
    data?.visitor?.userAccount?.contraMagicBioSuggestions?.map(
      (suggestion) => ({
        id: suggestion.id,
        isSelected: suggestion.isSelected,
        suggestion: suggestion.suggestion,
      }),
    ) ?? [];

  const contraMagicQuestionnaireResponse = {
    whatMakesYouUnique:
      data?.visitor?.userAccount?.contraMagicQuestionnaireResponse
        ?.whatMakesYouUnique,
    whatServicesDoYouOffer:
      data?.visitor?.userAccount?.contraMagicQuestionnaireResponse
        ?.whatServicesDoYouOffer,
  };

  useMount(() => {
    if (addBio && inputRef) {
      inputRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  });

  useEffect(() => {
    setIsInlineEditing(addBio);
  }, [addBio]);

  const bioSuggestionsRef = useRef<HTMLDivElement>(null);
  const [isBioSuggestionsOpen, setIsBioSuggestionsOpen] = useState(false);

  const { hideProfileMagicBioIndicator, shouldHideProfileMagicBioIndicator } =
    useProfileMagicBioIndicator();

  const userHasBioSuggestions = bioSuggestions.length > 0;

  const onClickHoverToEditContainer = useCallback(() => {
    if (!isInlineEditing) {
      setIsInlineEditing(true);

      if (userHasBioSuggestions) {
        setIsBioSuggestionsOpen(true);
        hideProfileMagicBioIndicator();
      }
    }
  }, [hideProfileMagicBioIndicator, isInlineEditing, userHasBioSuggestions]);

  const hasSeenProfileMagicRevealAnimation = useHasUserInterfaceFlags(
    'HIDE_FTUE_CONTRA_PROFILE_MAGIC_REVEAL_ANIMATION',
  );
  const shouldShowProfileMagicRevealAnimation =
    !hasSeenProfileMagicRevealAnimation && userHasBioSuggestions;

  // during the reveal animation, we want to strip out any emojis as they would be solid black due to the black background clip
  const bioValue = shouldShowProfileMagicRevealAnimation
    ? omitEmojisFromText(bio)
    : bio;

  const visitorCanEditDefaultState = bioValue ? (
    <InlineEditContainer
      label="Edit your bio"
      onClick={onClickHoverToEditContainer}
      visitorCanEdit
    >
      <BioTextRegular
        $startRevealBioAnimation={shouldShowProfileMagicRevealAnimation}
      >
        {bioValue}
      </BioTextRegular>
      <EditIcon size={16} />
    </InlineEditContainer>
  ) : (
    <InlineEditingEmptyPlaceholder
      label="Add a descriptive bio"
      onClick={onClickHoverToEditContainer}
    />
  );

  return (
    <BioContainer ref={bioSuggestionsRef}>
      {isInlineEditing ? (
        <Suspense fallback={visitorCanEditDefaultState}>
          <LazyBioV2InlineEditing
            bio={bio}
            bioSuggestions={bioSuggestions}
            bioSuggestionsRef={bioSuggestionsRef}
            hideProfileMagicBioIndicator={hideProfileMagicBioIndicator}
            isBioSuggestionsOpen={isBioSuggestionsOpen}
            isInlineEditing={isInlineEditing}
            questionnaireResponse={contraMagicQuestionnaireResponse}
            setIsBioSuggestionsOpen={setIsBioSuggestionsOpen}
            setIsInlineEditing={setIsInlineEditing}
            shouldHideProfileMagicBioIndicator={
              shouldHideProfileMagicBioIndicator
            }
            userHasBioSuggestions={userHasBioSuggestions}
          />
        </Suspense>
      ) : (
        visitorCanEditDefaultState
      )}
    </BioContainer>
  );
};
